import { useEffect, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import { Button, Checkbox, Tag } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { StoreKind } from "@/lib/api/types";
import { initialsOf } from "./helpers";

/**
 * Small pieces the sellers screens need that the admin kit (`@/shared/rd`)
 * does not have yet. Kept here, not in the kit, so the kit stays the lead's.
 */

/** The design's avatar tints. Tokens, so they hold in both themes. */
const AVATAR_TINTS = [
  "var(--rd-blue-tag)",
  "var(--rd-green-tag)",
  "var(--rd-purple-tag)",
  "var(--rd-amber-tag)",
  "var(--rd-pink-tag)",
  "var(--rd-orange-tag)",
];

/**
 * Stable per-seller tint. Keyed on the id, not the row's position, so a seller
 * keeps its colour across pages, searches and reloads.
 */
function tintFor(id: string) {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return AVATAR_TINTS[hash % AVATAR_TINTS.length];
}

/** 28px disc: the first store logo when there is one, else tinted initials. */
export function SellerAvatar({
  id,
  name,
  logoUrl,
  size = 28,
}: {
  id: string;
  name: string;
  logoUrl?: string | null;
  size?: number;
}) {
  const frame = {
    width: size,
    height: size,
    flex: "none",
    borderRadius: "50%",
  } as const;
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        aria-hidden="true"
        style={{ ...frame, objectFit: "cover", background: "var(--rd-surface-soft)" }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        ...frame,
        background: tintFor(id),
        color: "var(--rd-text)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.4),
        fontWeight: 600,
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

/** "Online store" (blue) or "Restaurant" (orange). */
export function StoreKindTag({ kind }: { kind: StoreKind }) {
  const t = useTranslations();
  return kind === "RESTAURANT" ? (
    <Tag tone="orange">{t(keys.admin.restaurantKindLabel)}</Tag>
  ) : (
    <Tag tone="blue">{t(keys.admin.onlineStoreKindLabel)}</Tag>
  );
}

/**
 * The toolbar's "Columns" control: which optional columns the table draws.
 * The kit has no popover yet, so this is a small anchored panel.
 */
export function ColumnsMenu({
  columns,
  hidden,
  onToggle,
}: {
  columns: { key: string; label: string }[];
  hidden: ReadonlySet<string>;
  onToggle: (key: string) => void;
}) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <Button
        variant="ghost"
        size="sm"
        icon={Columns3}
        muted
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        {t(keys.common.columns)}
      </Button>
      {open ? (
        <div
          role="group"
          aria-label={t(keys.common.displayOptions)}
          style={{
            position: "absolute",
            insetInlineEnd: 0,
            top: "calc(100% + 4px)",
            zIndex: 20,
            minWidth: 200,
            padding: 8,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            borderRadius: 8,
            background: "var(--rd-surface-raised)",
            boxShadow: "var(--rd-shadow-modal)",
          }}
        >
          {columns.map((column) => (
            <Checkbox
              key={column.key}
              label={column.label}
              checked={!hidden.has(column.key)}
              onChange={() => onToggle(column.key)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
